import type { StateCreator } from 'zustand'
import type { SessionEvent, OrchestratorStatus } from '../../types'

export interface OrchestratorSlice {
  // State
  orchestratorEvents: SessionEvent[]
  orchestratorStatus: OrchestratorStatus
  orchestratorLastActiveAt: string | null
  orchestratorConnected: boolean
  orchestratorLoading: boolean
  orchestratorError: string | null

  // Actions
  setOrchestratorConnected: (connected: boolean) => void
  setOrchestratorStatus: (status: OrchestratorStatus) => void
  addOrchestratorEvent: (event: SessionEvent) => void
  setOrchestratorEvents: (events: SessionEvent[]) => void
  mergeOrchestratorEvents: (events: SessionEvent[]) => void
  clearOrchestratorEvents: () => void
  setOrchestratorError: (error: string | null) => void
  setOrchestratorLoading: (loading: boolean) => void
}

const MAX_EVENTS = 100 // Keep last N events to prevent memory bloat

export const createOrchestratorSlice: StateCreator<OrchestratorSlice, [], [], OrchestratorSlice> = (set, get) => ({
  orchestratorEvents: [],
  orchestratorStatus: 'idle',
  orchestratorLastActiveAt: null,
  orchestratorConnected: false,
  orchestratorLoading: false,
  orchestratorError: null,

  setOrchestratorConnected: (connected) => {
    set({
      orchestratorConnected: connected,
      orchestratorStatus: connected ? 'watching' : 'disconnected',
    })
  },

  setOrchestratorStatus: (status) => {
    set({ orchestratorStatus: status })
  },

  addOrchestratorEvent: (event) => {
    const { orchestratorEvents } = get()

    // Deduplicate by id
    if (orchestratorEvents.some((e) => e.id === event.id)) {
      return
    }

    // Add new event and cap at MAX_EVENTS
    const updated = [...orchestratorEvents, event].slice(-MAX_EVENTS)

    // Derive status from event type
    let status: OrchestratorStatus = 'watching'
    if (event.type === 'observation') status = 'watching'
    else if (event.type === 'reasoning') status = 'reasoning'
    else if (event.type === 'action') status = 'acting'
    else if (event.type === 'waiting') status = 'waiting'
    else if (event.type === 'error') status = 'watching' // Fall back to watching on error
    else if (event.type === 'context') status = 'watching' // Context loading doesn't change status

    set({
      orchestratorEvents: updated,
      orchestratorStatus: status,
      orchestratorLastActiveAt: event.timestamp,
    })
  },

  setOrchestratorEvents: (events) => {
    set({
      orchestratorEvents: events.slice(-MAX_EVENTS),
      orchestratorLastActiveAt: events.length > 0 ? events[events.length - 1].timestamp : null,
    })
  },

  mergeOrchestratorEvents: (newEvents) => {
    const { orchestratorEvents } = get()

    // Merge: add new events that don't exist (by id), sort by timestamp
    const existingIds = new Set(orchestratorEvents.map((e) => e.id))
    const uniqueNew = newEvents.filter((e) => !existingIds.has(e.id))
    const merged = [...orchestratorEvents, ...uniqueNew]
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      .slice(-MAX_EVENTS)

    set({
      orchestratorEvents: merged,
      orchestratorLastActiveAt: merged.length > 0 ? merged[merged.length - 1].timestamp : null,
    })
  },

  clearOrchestratorEvents: () => {
    set({ orchestratorEvents: [], orchestratorLastActiveAt: null })
  },

  setOrchestratorError: (error) => {
    set({ orchestratorError: error })
  },

  setOrchestratorLoading: (loading) => {
    set({ orchestratorLoading: loading })
  },
})
